import { print } from 'graphql';
import type { FetchResult, Operation } from '@apollo/client';
import { ApolloLink, Observable } from '@apollo/client';
import { MessageTypes } from '@dva/frameio-uxp-messaging';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { auth, isTest } from '@frameio/utils';

const LOG_EVENTS = {
  GRAPHQL_OPERATION_SUCCESS: 'uxp_graphql_operation_success',
  GRAPHQL_OPERATION_ERROR: 'uxp_graphql_operation_error',
  GRAPHQL_SUBSCRIPTION_DATA: 'uxp_graphql_subscription_data',
  GRAPHQL_SUBSCRIPTION_COMPLETE: 'uxp_graphql_subscription_complete',
  GRAPHQL_SUBSCRIPTION_ERROR: 'uxp_graphql_subscription_error',
  GRAPHQL_OPERATION_MISSING_RESOLVER: 'uxp_graphql_operation_missing_resolver',
};

type OperationLogData = {
  operationName: string;
};
type UxpHostInfo = {
  name: string;
  pluginVersion: string;
  schemaVersion: string;
  version: string;
};

type MissingResolverCategory =
  | 'MISSING_QUERY_OR_MUTATION_FIELD'
  | 'MISSING_SUBSCRIPTION'
  | 'FIELD_MISSING_RESOLVER';

type MissingResolverDetection = {
  matched: boolean;
  category?: MissingResolverCategory;
  fieldPath?: string;
};

const detectMissingResolverError = (
  message: string
): MissingResolverDetection => {
  if (!message) return { matched: false };

  const nonNullableMatch = message.match(
    /Cannot return null for non-nullable field (\w+)\.(\w+)/i
  );
  if (nonNullableMatch) {
    const fieldPath = `${nonNullableMatch[1]}.${nonNullableMatch[2]}`;
    return {
      matched: true,
      category: 'MISSING_QUERY_OR_MUTATION_FIELD',
      fieldPath,
    };
  }

  if (/Subscription field must return Async Iterable/i.test(message)) {
    return {
      matched: true,
      category: 'MISSING_SUBSCRIPTION',
    };
  }

  return { matched: false };
};
/**
 * This is a terminating Apollo Link which is responsible for handling all GraphQL operations
 * via postMessage and eventListeners between the next-integrations and a UXP plugin inside an Adobe app.
 *
 * When a request comes in, it persists observable resolution functions in a Map keyed
 * by a random UUID. That UUID is returned in the result message and is used to resolve or
 * reject the request.
 */
export class UXPLink extends ApolloLink {
  private requests = new Map<
    string,
    {
      resolve: (value: FetchResult) => void;
      reject: (reason: any) => void;
      startTime: number;
      operationLogData: OperationLogData;
    }
  >();

  private subscriptions = new Map<
    string,
    {
      observers: Set<any>;
      count: number;
      operationKey: string;
      operationLogData: OperationLogData;
      rootFieldPaths: string[];
    }
  >();

  private messageHandler: (event: MessageEvent) => void;

  // Populated asynchronously at startup
  private hostInfo: UxpHostInfo | null = null;

  private hostInfoReady: Promise<void>;

  private hostInfoResolved = false;

  private resolveHostInfoReady!: () => void;

  private userId?: string;

  private accountId?: string;

  constructor() {
    super();
    this.messageHandler = this.handleMessage.bind(this);
    this.setupMessageListener();

    // Prepare readiness promise and fetch host info ASAP on client
    this.hostInfoReady = new Promise<void>((resolve) => {
      this.resolveHostInfoReady = resolve;
    });
    if (typeof window !== 'undefined') {
      // Kick off initialization, but don't block constructor
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.initializeHostInfo();
      this.updateUserAccountInfo();
    }
  }

  private async initializeHostInfo() {
    try {
      const result = await this.sendMessage(
        {
          type: MessageTypes.GRAPHQL_REQUEST,
          operation: `
            query GetUxpHostInfo {
              uxpHostInfo {
                name
                version
                pluginVersion
                schemaVersion
              }
            }
          `,
          operationName: 'GetUxpHostInfo',
          variables: {},
        },
        { timeoutMs: 5000, suppressLogging: true }
      );

      this.hostInfo = result?.data?.uxpHostInfo ?? null;
    } catch (_) {
      this.hostInfo = null;
    } finally {
      this.hostInfoResolved = true;
      if (this.resolveHostInfoReady) this.resolveHostInfoReady();
    }
  }

  private updateUserAccountInfo() {
    if (!this.userId) {
      this.userId = auth.getAccessTokenUserId(auth.getAccessToken() || '');
    }
    // appComponentStorage is in shared, which we cannot add as a dependency.
    // eslint-disable-next-line no-storage/no-browser-storage
    const rawAccountId = localStorage.getItem('lastViewedAccountId');
    try {
      this.accountId = rawAccountId
        ? (JSON.parse(rawAccountId ?? '') as string)
        : undefined;
    } catch {
      this.accountId = undefined;
    }
  }

  private getUserAccountInfo(): {
    userId?: string;
    accountId?: string;
  } {
    return {
      userId: this.userId,
      accountId: this.accountId,
    };
  }

  public getClientIdentifier(): {
    client: string;
    clientName: string;
    clientVersion: string;
  } {
    const clientName = (global || window).location.hostname;
    const releaseVersion = process.env.RELEASE_VERSION || 'unknown';
    const clientVersion = releaseVersion.split('@').pop() || 'unknown';

    return {
      client: `${clientName}/${clientVersion}`,
      clientName,
      clientVersion,
    };
  }

  /**
   * Public method to get the UXP host information.
   * Returns null if host info is not available yet or failed to load.
   */
  public getHostInfo(): UxpHostInfo | null {
    return this.hostInfo;
  }

  /**
   * Public method to send a raw UXP message and get a Promise-based response.
   * This allows external code (like handleUXPRefresh) to reuse the message handling infrastructure.
   */
  public sendMessage(
    message: {
      type: string;
      operation: string;
      operationName: string;
      variables?: Record<string, any>;
    },
    options: {
      timeoutMs?: number;
      suppressLogging?: boolean;
    } = {}
  ): Promise<any> {
    const { timeoutMs = 10000, suppressLogging = false } = options;
    const id = crypto.randomUUID();

    return new Promise((resolve, reject) => {
      let timeoutId: NodeJS.Timeout;

      const cleanup = () => {
        this.requests.delete(id);
        if (timeoutId) clearTimeout(timeoutId);
      };

      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timed-out waiting for UXP message response'));
      }, timeoutMs);

      const startTime = Date.now();
      const operationLog: OperationLogData = {
        operationName: message.operationName,
      };

      const baseHandlers = {
        resolve: (result: any) => {
          cleanup();
          resolve(result);
        },
        reject: (error: any) => {
          cleanup();
          reject(error);
        },
      } as const;

      const handlers = suppressLogging
        ? baseHandlers
        : this.withOperationLogging(operationLog, startTime, baseHandlers);

      this.requests.set(id, {
        resolve: handlers.resolve,
        reject: handlers.reject,
        startTime,
        operationLogData: operationLog,
      });

      window?.uxpHost?.postMessage(
        {
          ...message,
          id,
        },
        '*'
      );
    });
  }

  private handleMessage(event: MessageEvent) {
    if (!isValidOrigin(event.origin) && !isTest()) {
      return;
    }
    switch (event.data.type) {
      case MessageTypes.GRAPHQL_RESPONSE: {
        const { id, result } = event.data;
        const request = this.requests.get(id);
        if (request) {
          const hasErrors = result.errors && result.errors.length > 0;

          if (hasErrors) {
            const firstMessage =
              result.errors?.[0]?.message ?? 'Unknown GraphQL error';
            request.reject(new Error(firstMessage));
          } else {
            request.resolve(result);
          }

          this.requests.delete(id);
        }
        break;
      }
      case MessageTypes.GRAPHQL_ERROR: {
        const { id, error } = event.data;
        const request = this.requests.get(id);
        if (request) {
          const firstMessage = error?.message ?? 'Unknown GraphQL error';

          // eslint-disable-next-line no-console
          console.error('[UXPLink] GraphQL Error:', error);
          request.reject(new Error(firstMessage));
          this.requests.delete(id);
        }
        break;
      }
      case MessageTypes.GRAPHQL_SUBSCRIPTION_DATA: {
        const { id, result } = event.data;
        const subscription = this.subscriptions.get(id);
        if (subscription) {
          subscription.observers.forEach((observer) => {
            observer.next(result);
          });
        }
        break;
      }
      case MessageTypes.GRAPHQL_SUBSCRIPTION_COMPLETE: {
        const { id } = event.data;
        const subscription = this.subscriptions.get(id);

        if (subscription) {
          subscription.observers.forEach((observer) => {
            observer.complete();
          });
          this.subscriptions.delete(id);
        }
        break;
      }
      case MessageTypes.GRAPHQL_SUBSCRIPTION_ERROR: {
        const { id, error } = event.data;
        const subscription = this.subscriptions.get(id);
        if (subscription) {
          const firstMessage = error?.message ?? '';
          this.logSubscriptionError(
            subscription.operationLogData,
            firstMessage,
            subscription.rootFieldPaths
          );

          // eslint-disable-next-line no-console
          console.error('[UXPLink] GraphQL Subscription Error:', error);
          subscription.observers.forEach((observer) => {
            observer.error(new Error(error.message));
          });
          this.subscriptions.delete(id);
        }
        break;
      }
      default: {
        // @todo (FCI-96): look into observability of unknown message types
        break;
      }
    }
  }

  private setupMessageListener() {
    if (typeof window === 'undefined') return;
    window.addEventListener('message', (event) => this.handleMessage(event));
  }

  private removeMessageListener() {
    if (typeof window === 'undefined') return;
    window.removeEventListener('message', this.messageHandler);
  }

  public dispose() {
    this.removeMessageListener();
    this.requests.clear();
    this.subscriptions.clear();
  }

  request(operation: Operation) {
    return new Observable<FetchResult>((observer) => {
      const id = crypto.randomUUID();

      const isSubscription = operation.query.definitions.some(
        (def) =>
          def.kind === 'OperationDefinition' && def.operation === 'subscription'
      );
      if (!isSubscription) {
        return this.handleQueryOrMutation(id, operation, observer);
      }

      return this.handleSubscription(id, operation, observer);
    });
  }

  private handleQueryOrMutation(
    id: string,
    operation: Operation,
    observer: any
  ) {
    const startTime = Date.now();
    const operationLog: OperationLogData = {
      operationName: operation.operationName,
    };

    const wrapped = this.withOperationLogging(operationLog, startTime, {
      resolve: (result: any) => {
        observer.next(result);
        observer.complete();
      },
      reject: (error: any) => {
        // eslint-disable-next-line no-console
        console.error('[UXPLink] Query/Mutation Error:', error);
        observer.error(error);
      },
    });

    this.requests.set(id, {
      resolve: wrapped.resolve,
      reject: wrapped.reject,
      startTime,
      operationLogData: operationLog,
    });

    window?.uxpHost?.postMessage(
      {
        type: MessageTypes.GRAPHQL_REQUEST,
        id,
        operation: print(operation.query),
        variables: operation.variables,
      },
      '*'
    );

    return () => {
      this.requests.delete(id);
    };
  }

  private handleSubscription(id: string, operation: Operation, observer: any) {
    const operationKey = `${print(operation.query)}_${JSON.stringify(
      operation.variables || {}
    )}`;

    const existingSubscription = Array.from(this.subscriptions.entries()).find(
      ([__, sub]) => sub.operationKey === operationKey
    );

    if (existingSubscription) {
      const [existingId, subscription] = existingSubscription;
      subscription.observers.add(observer);
      subscription.count += 1;

      return () => {
        subscription.observers.delete(observer);
        subscription.count -= 1;

        if (subscription.count === 0) {
          window?.uxpHost?.postMessage(
            {
              type: MessageTypes.GRAPHQL_UNSUBSCRIBE,
              id: existingId,
            },
            '*'
          );
          this.subscriptions.delete(existingId);
        }
      };
    }

    this.subscriptions.set(id, {
      observers: new Set([observer]),
      count: 1,
      operationKey,
      operationLogData: {
        operationName: operation.operationName,
      },
      rootFieldPaths: this.getRootFieldPathsFromOperation(
        operation,
        'subscription'
      ),
    });

    window?.uxpHost?.postMessage(
      {
        type: MessageTypes.GRAPHQL_SUBSCRIBE,
        id,
        operation: print(operation.query),
        variables: operation.variables,
      },
      '*'
    );

    return () => {
      const subscription = this.subscriptions.get(id);
      if (!subscription) return;

      subscription.observers.delete(observer);
      subscription.count -= 1;

      if (subscription.count === 0) {
        window?.uxpHost?.postMessage(
          {
            type: MessageTypes.GRAPHQL_UNSUBSCRIBE,
            id,
          },
          '*'
        );
        this.subscriptions.delete(id);
      }
    };
  }

  private logOperationSuccess(operation: OperationLogData, duration: number) {
    datadogLogger.info(LOG_EVENTS.GRAPHQL_OPERATION_SUCCESS, {
      ...this.getClientIdentifier(),
      ...this.getUserAccountInfo(),
      uxpLink: {
        duration,
        operationName: operation.operationName,
        hostInfo: this.hostInfo ?? undefined,
      },
    });
  }

  private logOperationError(
    operationLogData: OperationLogData,
    errorMessage: string,
    duration: number
  ) {
    if (
      typeof window !== 'undefined' &&
      this.hostInfoReady &&
      !this.hostInfoResolved
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.hostInfoReady.finally(() => {
        this.logOperationError(operationLogData, errorMessage, duration);
      });
      return;
    }
    const detection = detectMissingResolverError(errorMessage);
    if (detection.matched && detection.category) {
      this.logMissingResolverEvent(
        operationLogData,
        { category: detection.category, fieldPath: detection.fieldPath },
        { durationMs: duration, errorMessage }
      );
    }
    datadogLogger.error(LOG_EVENTS.GRAPHQL_OPERATION_ERROR, {
      ...this.getClientIdentifier(),
      ...this.getUserAccountInfo(),
      uxpLink: {
        duration,
        operationName: operationLogData.operationName,
        hostInfo: this.hostInfo ?? undefined,
        error: errorMessage,
      },
    });
  }

  private logSubscriptionError(
    operationLogData: OperationLogData,
    errorMessage: string,
    fieldPathFallbacks?: string[]
  ) {
    if (
      typeof window !== 'undefined' &&
      this.hostInfoReady &&
      !this.hostInfoResolved
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.hostInfoReady.finally(() =>
        this.logSubscriptionError(operationLogData, errorMessage)
      );
      return;
    }
    const detection = detectMissingResolverError(errorMessage);
    if (detection.matched && detection.category) {
      if (
        !detection.fieldPath &&
        fieldPathFallbacks &&
        fieldPathFallbacks.length
      ) {
        fieldPathFallbacks.forEach((fp) =>
          this.logMissingResolverEvent(
            operationLogData,
            { category: detection.category!, fieldPath: fp },
            { errorMessage }
          )
        );
      } else {
        this.logMissingResolverEvent(
          operationLogData,
          { category: detection.category, fieldPath: detection.fieldPath },
          { errorMessage }
        );
      }
    }
    datadogLogger.error(LOG_EVENTS.GRAPHQL_SUBSCRIPTION_ERROR, {
      ...this.getClientIdentifier(),
      ...this.getUserAccountInfo(),
      uxpLink: {
        operationName: operationLogData.operationName,
        hostInfo: this.hostInfo ?? undefined,
        error: errorMessage,
      },
    });
  }

  private logMissingResolverEvent(
    operationLogData: OperationLogData,
    missing: { category: MissingResolverCategory; fieldPath?: string },
    options: { durationMs?: number; errorMessage?: string } = {}
  ) {
    const { durationMs, errorMessage } = options;

    if (
      typeof window !== 'undefined' &&
      this.hostInfoReady &&
      !this.hostInfoResolved
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.hostInfoReady.finally(() =>
        this.logMissingResolverEvent(operationLogData, missing, options)
      );
      return;
    }

    datadogLogger.error(LOG_EVENTS.GRAPHQL_OPERATION_MISSING_RESOLVER, {
      ...this.getClientIdentifier(),
      ...this.getUserAccountInfo(),
      uxpLink: {
        ...(typeof durationMs === 'number' ? { duration: durationMs } : {}),
        operationName: operationLogData.operationName,
        hostInfo: this.hostInfo ?? undefined,
        ...(errorMessage ? { error: errorMessage } : {}),
        missingResolver: {
          category: missing.category,
          ...(missing.fieldPath ? { fieldPath: missing.fieldPath } : {}),
        },
      },
    });
  }

  private withOperationLogging(
    operation: OperationLogData,
    startTime: number,
    handlers: { resolve: (result: any) => void; reject: (error: any) => void }
  ) {
    return {
      resolve: (result: any) => {
        const duration = Date.now() - startTime;
        this.logOperationSuccess(operation, duration);
        try {
          const missing = result?.extensions?.uxpLink?.missingResolvers;
          if (Array.isArray(missing) && missing.length > 0) {
            missing.forEach((fieldPath: string) =>
              this.logMissingResolverEvent(
                operation,
                { category: 'FIELD_MISSING_RESOLVER', fieldPath },
                { durationMs: duration }
              )
            );
          }
        } catch (_) {
          // Best-effort logging; do not affect resolution path
        }
        handlers.resolve(result);
        this.updateUserAccountInfo();
      },
      reject: (error: any) => {
        const duration = Date.now() - startTime;
        const message = (error?.message ?? String(error)) as string;
        this.logOperationError(operation, message, duration);
        handlers.reject(error);
        this.updateUserAccountInfo();
      },
    };
  }

  private getRootFieldPathsFromOperation(
    operation: Operation,
    operationType: 'subscription' | 'query' | 'mutation'
  ): string[] {
    try {
      const opDef = operation.query.definitions.find(
        (def: any) =>
          def.kind === 'OperationDefinition' && def.operation === operationType
      ) as any;
      if (
        !opDef ||
        !opDef.selectionSet ||
        !Array.isArray(opDef.selectionSet.selections)
      ) {
        return [];
      }
      const fields = opDef.selectionSet.selections
        .filter(
          (sel: any) => sel.kind === 'Field' && sel.name && sel.name.value
        )
        .map((sel: any) =>
          `
Subscription.${sel.name.value}`.trim()
        );
      return fields;
    } catch (_) {
      return [];
    }
  }
}

/**
 * This utility validates the origin of a message to ensure it is from a valid host.
 *
 * A valid host is either a valid UXP plugin id or a Frame.io domain (for E2E testing)
 * @param url
 * @returns
 */
const VALID_UXP_PLUGIN_IDS = ['com.adobe.frameio.uxp'];
const isValidOrigin = (url: string): boolean => {
  if (VALID_UXP_PLUGIN_IDS.includes(url)) return true;
  try {
    if (!url) return false;
    const hostUrl = new URL(url);
    if (hostUrl.hostname.endsWith('.frame.io')) return true;
    if (hostUrl.hostname.endsWith('-frameio.vercel.app')) return true;
    return false;
  } catch (e) {
    return false;
  }
  return false;
};

// Singleton instance for shared usage between UXPLink and handleUXPRefresh
let sharedUXPLinkInstance: UXPLink | null = null;

export const getSharedUXPLink = (): UXPLink => {
  if (typeof window === 'undefined') {
    throw new Error('UXPLink can only be used in browser environment');
  }

  if (!sharedUXPLinkInstance) {
    sharedUXPLinkInstance = new UXPLink();
  }
  return sharedUXPLinkInstance;
};

const uxpLink = (): UXPLink | undefined => {
  if (typeof window !== 'undefined') {
    return getSharedUXPLink();
  }
  return undefined;
};

export default uxpLink;
